import { useMemo, useState } from 'react';
import {
  Home,
  Search,
  X,
  Bus,
  Sun,
  Bath,
  Wallet,
  BadgeCheck,
  MessageCircle,
  Building2,
} from 'lucide-react';
import AccommodationCard from '../components/AccommodationCard';
import accommodations from './accommodationDetails';

const WHATSAPP = '27729792961';

const CAMPUSES = [
  { id: 'all', label: 'All areas' },
  { id: 'VUT', label: 'Near VUT', test: /vut/i },
  { id: 'NWU', label: 'Near NWU', test: /nwu|north.?west/i },
  { id: 'Sedibeng', label: 'Near Sedibeng', test: /sedibeng/i },
];

const FEATURES = [
  { id: 'transport', label: 'Transport to campus', icon: Bus, test: /transport|\bbus\b/i },
  { id: 'power', label: 'Backup power', icon: Sun, test: /solar|generator/i },
  { id: 'ensuite', label: 'En-suite', icon: Bath, test: /en.?suite/i },
  { id: 'deposit', label: 'No deposit', icon: Wallet, test: /no deposit/i },
];

const fullText = (a) => `${a.name} ${a.location} ${a.price} ${a.distance} ${a.paymentType}`;
const campusText = (a) => `${a.distance} ${a.price}`;

const waLink = (message) => `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(message)}`;

export default function Accommodation() {
  // .filter(Boolean) skips any empty slots in the data list
  const listings = useMemo(() => accommodations.filter(Boolean), []);

  const [query, setQuery] = useState('');
  const [campus, setCampus] = useState('all');
  const [features, setFeatures] = useState([]);

  const nsfasCount = listings.filter((a) => /n[sf]{2}as/i.test(fullText(a))).length;

  // Only show campus chips that at least one listing matches
  const campusOptions = CAMPUSES.filter(
    (c) => c.id === 'all' || listings.some((a) => c.test.test(campusText(a)))
  );
  const featureOptions = FEATURES.filter((f) => listings.some((a) => f.test.test(fullText(a))));

  const toggleFeature = (id) =>
    setFeatures((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));

  const resetFilters = () => {
    setQuery('');
    setCampus('all');
    setFeatures([]);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const campusRule = CAMPUSES.find((c) => c.id === campus);
    return listings.filter((a) => {
      if (campusRule?.test && !campusRule.test.test(campusText(a))) return false;
      const text = fullText(a);
      if (!features.every((id) => FEATURES.find((f) => f.id === id).test.test(text))) return false;
      if (q && !`${a.name} ${a.location}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [listings, query, campus, features]);

  const hasFilters = query || campus !== 'all' || features.length > 0;

  const chip = (active) =>
    `inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition ${
      active
        ? 'border-yellow-400 bg-yellow-400 text-gray-900'
        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-400'
    }`;

  return (
    <section id="accommodation" className="bg-white px-4 py-16">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-yellow-100">
            <Home className="h-7 w-7 text-yellow-800" />
          </div>
          <h1 className="mb-3 text-3xl font-bold text-gray-900 sm:text-4xl">Student Accommodation</h1>
          <p className="text-lg text-gray-600">
            We help students find quality accommodation near their universities in Vanderbijlpark.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-sm">
            <span className="rounded-full bg-gray-100 px-3 py-1 font-medium text-gray-700">
              {listings.length} places
            </span>
            {nsfasCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-3 py-1 font-medium text-yellow-800">
                <BadgeCheck className="h-4 w-4" />
                {nsfasCount === listings.length ? 'All' : nsfasCount} NSFAS accredited
              </span>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="mx-auto mb-5 max-w-xl">
          <label className="relative block">
            <span className="sr-only">Search by name or street</span>
            <Search className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or street"
              className="w-full rounded-full border border-gray-200 bg-white py-3.5 pl-12 pr-5 text-gray-900 shadow-md outline-none transition placeholder:text-gray-400 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/40"
            />
          </label>
        </div>

        {/* Filters */}
        <div className="-mx-4 mb-8 overflow-x-auto px-4">
          <div className="mx-auto flex w-max gap-2 pb-1">
            {campusOptions.map((c) => (
              <button key={c.id} onClick={() => setCampus(c.id)} className={chip(campus === c.id)}>
                {c.label}
              </button>
            ))}
            {featureOptions.length > 0 && <span className="mx-1 w-px self-stretch bg-gray-200" />}
            {featureOptions.map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => toggleFeature(id)} className={chip(features.includes(id))}>
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Result count */}
        <div className="mb-6 flex items-center justify-between text-sm text-gray-600">
          <p>
            Showing <span className="font-semibold text-gray-900">{filtered.length}</span> of {listings.length} places
          </p>
          {hasFilters && (
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1 font-semibold text-gray-900 underline underline-offset-2"
            >
              <X className="h-4 w-4" />
              Clear filters
            </button>
          )}
        </div>

        {/* Listings */}
        {filtered.length > 0 ? (
          <div className="mb-16 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((accommodation) => (
              <AccommodationCard key={accommodation.name} {...accommodation} />
            ))}
          </div>
        ) : (
          <div className="mb-16 rounded-2xl border border-dashed border-gray-300 px-6 py-14 text-center">
            <p className="text-lg font-semibold text-gray-900">No places match those filters</p>
            <p className="mt-1 text-gray-600">Try removing a filter, or ask us on WhatsApp and we'll help you find one.</p>
            <button
              onClick={resetFilters}
              className="mt-5 rounded-xl bg-yellow-400 px-5 py-2.5 font-semibold text-gray-900 transition hover:bg-yellow-500"
            >
              Show all places
            </button>
          </div>
        )}

        {/* Call-to-action panels */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col rounded-2xl bg-gray-50 p-6 sm:p-8">
            <MessageCircle className="h-8 w-8 text-gray-900" />
            <h3 className="mt-4 text-xl font-semibold text-gray-900">Can't find the right place?</h3>
            <p className="mt-2 flex-1 text-gray-600">
              Listings are subject to availability. Message us and we'll match you with a room near your campus.
            </p>
            <a
              href={waLink("Hi! I'm interested in student accommodation in Vanderbijlpark.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center justify-center gap-2 self-start rounded-xl bg-green-600 px-6 py-3 font-semibold text-white transition hover:bg-green-700"
            >
              <MessageCircle className="h-5 w-5" />
              Enquire on WhatsApp
            </a>
          </div>

          <div className="flex flex-col rounded-2xl bg-yellow-100 p-6 sm:p-8">
            <Building2 className="h-8 w-8 text-yellow-800" />
            <h3 className="mt-4 text-xl font-semibold text-gray-900">Own a student residence?</h3>
            <p className="mt-2 flex-1 text-gray-700">
              List your accommodation with V2V and reach VUT, NWU and Sedibeng students looking for a place to stay.
            </p>
            <a
              href={waLink("Hi! I'd like to list my student accommodation with V2V.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center justify-center gap-2 self-start rounded-xl bg-gray-900 px-6 py-3 font-semibold text-white transition hover:bg-gray-800"
            >
              List your accommodation
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
